import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import AuthorFormDialog from '../components/AuthorFormDialog';

const api = () => process.env.REACT_APP_BACKEND_ADDRESS;
const authHeaders = () => ({ 'Authorization': `Bearer ${Cookies.get('token')}`, 'Content-Type': 'application/json' });

const InviteDialog = ({ open, onClose }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // { invite_url, email_sent }
    const [copied, setCopied] = useState(false);

    const handleSend = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${api()}/invite`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ email: email.trim() || null }),
            });
            const data = await res.json();
            setResult(data);
        } catch {
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(result.invite_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClose = () => {
        setEmail(''); setResult(null); setCopied(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><PersonAddIcon sx={{ color: 'primary.main' }} />Invite Someone</DialogTitle>
            <DialogContent>
                {!result ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            fullWidth label="Email (optional)" type="email"
                            value={email} onChange={e => setEmail(e.target.value)}
                            helperText="If provided, we'll send the invite link by email as well."
                            disabled={loading}
                        />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {result.email_sent && (
                            <Alert severity="success" icon={<MailOutlineIcon />}>
                                Invite sent to {email}
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Share this link — it expires in 7 days and can only be used once.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth size="small" value={result.invite_url}
                                InputProps={{ readOnly: true }}
                                sx={{ '& input': { fontSize: 12 } }}
                            />
                            <Button
                                variant="outlined" size="small" startIcon={<ContentCopyIcon />}
                                onClick={handleCopy} sx={{ flexShrink: 0 }}
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
                {!result && (
                    <Button variant="contained" onClick={handleSend} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Invite'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

const AdminPage = () => {
    const storedUser = JSON.parse(localStorage.getItem('user')) || {};
    const isAdmin = storedUser.role === 'admin';

    const [authors, setAuthors] = useState([]);
    const [authorSearch, setAuthorSearch] = useState('');
    const [authorDialog, setAuthorDialog] = useState({ open: false, author: null });
    const [inviteDialog, setInviteDialog] = useState(false);

    const [allUsers, setAllUsers] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [deletedRecipes, setDeletedRecipes] = useState([]);
    const [restoringId, setRestoringId] = useState(null);
    const [deletedSearch, setDeletedSearch] = useState('');

    const isDev = /localhost|127\.0\.0\.1|-dev-/.test(process.env.REACT_APP_BACKEND_ADDRESS);
    const [devColorOverride, setDevColorOverride] = useState(() => localStorage.getItem('devColorOverride') !== 'false');
    const handleDevColorToggle = (e) => {
        const val = e.target.checked;
        setDevColorOverride(val);
        localStorage.setItem('devColorOverride', val);
        window.dispatchEvent(new Event('storage'));
    };

    useEffect(() => {
        fetch(`${api()}/authors`, { headers: authHeaders() })
            .then(r => r.json())
            .then(setAuthors)
            .catch(() => {});

        if (isAdmin) {
            fetch(`${api()}/users`, { headers: authHeaders() })
                .then(r => r.json())
                .then(data => setAllUsers(data.users || []))
                .catch(() => {});
            fetch(`${api()}/admin/deleted-recipes`, { headers: authHeaders() })
                .then(r => r.json())
                .then(data => setDeletedRecipes(data.recipes || []))
                .catch(() => {});
        }
    }, [isAdmin]);

    const handleRestore = async (recipeId) => {
        setRestoringId(recipeId);
        try {
            await fetch(`${api()}/recipe/${recipeId}/restore`, { method: 'POST', headers: authHeaders() });
            setDeletedRecipes(prev => prev.filter(r => r.id !== recipeId));
        } finally {
            setRestoringId(null);
        }
    };

    const handleRoleChange = async (userId, role) => {
        setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
        try {
            await fetch(`${api()}/users/${userId}/role`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ role }),
            });
        } catch {
            fetch(`${api()}/users`, { headers: authHeaders() })
                .then(r => r.json())
                .then(data => setAllUsers(data.users || []))
                .catch(() => {});
        }
    };

    const filteredAuthors = authors.filter(a =>
        a.name.toLowerCase().includes(authorSearch.toLowerCase())
    );

    const filteredUsers = allUsers.filter(u =>
        `${u.first_name} ${u.last_name} ${u.username}`.toLowerCase().includes(userSearch.toLowerCase())
    );

    return (
        <Container maxWidth="sm" sx={{ mt: 6, pb: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Admin</Typography>
                <Button
                    startIcon={<MailOutlineIcon />}
                    size="small"
                    variant="outlined"
                    onClick={() => setInviteDialog(true)}
                >
                    Invite
                </Button>
            </Box>

            {/* Manage Authors */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>Authors</Typography>
                <Button
                    startIcon={<AddIcon />}
                    size="small"
                    variant="outlined"
                    onClick={() => setAuthorDialog({ open: true, author: null })}
                >
                    New Author
                </Button>
            </Box>

            <TextField
                fullWidth
                size="small"
                placeholder="Search authors…"
                value={authorSearch}
                onChange={(e) => setAuthorSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
            />

            {filteredAuthors.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {authorSearch ? 'No authors match your search.' : 'No author profiles yet.'}
                </Typography>
            ) : (
                <Box sx={{ mb: 3, maxHeight: 336, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {filteredAuthors.map((a, i) => (
                        <Box
                            key={a.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                py: 1,
                                px: 1.5,
                                borderBottom: i < filteredAuthors.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                            }}
                        >
                            <Avatar
                                src={a.avatar_url || '/default-author.svg'}
                                sx={{ width: 36, height: 36 }}
                            />
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{a.name}</Typography>
                            <IconButton size="small" onClick={() => setAuthorDialog({ open: true, author: a })}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}

            <AuthorFormDialog
                open={authorDialog.open}
                author={authorDialog.author}
                onClose={() => setAuthorDialog({ open: false, author: null })}
                onSaved={(saved) => setAuthors(prev => {
                    const exists = prev.find(a => a.id === saved.id);
                    return exists
                        ? prev.map(a => a.id === saved.id ? saved : a)
                        : [...prev, saved].sort((a, b) => a.name.localeCompare(b.name));
                })}
            />

            <InviteDialog open={inviteDialog} onClose={() => setInviteDialog(false)} />

            {/* Manage Users — admin only */}
            {isAdmin && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Users</Typography>

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search users…"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        }}
                    />

                    <Box sx={{ maxHeight: 336, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {filteredUsers.map((u, i) => (
                            <Box
                                key={u.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 1,
                                    px: 1.5,
                                    borderBottom: i < filteredUsers.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                }}
                            >
                                <Avatar
                                    src={u.avatar_url || undefined}
                                    sx={{ width: 36, height: 36, fontSize: '0.9rem' }}
                                >
                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                        {u.first_name} {u.last_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        @{u.username}
                                    </Typography>
                                </Box>
                                <Select
                                    size="small"
                                    value={u.role || 'member'}
                                    onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                    sx={{ minWidth: 110 }}
                                    disabled={u.id === storedUser.id}
                                >
                                    <MenuItem value="member">Member</MenuItem>
                                    <MenuItem value="editor">Editor</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                </Select>
                            </Box>
                        ))}
                    </Box>
                </>
            )}

            {isDev && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Dev Options</Typography>
                    <FormControlLabel
                        control={<Switch checked={devColorOverride} onChange={handleDevColorToggle} />}
                        label="Dev navbar color override"
                    />
                </>
            )}

            {isAdmin && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Deleted Recipes</Typography>
                    {deletedRecipes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No deleted recipes.</Typography>
                    ) : (() => {
                        const filtered = deletedRecipes.filter(r =>
                            r.title.toLowerCase().includes(deletedSearch.toLowerCase())
                        );
                        return (
                            <>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search deleted recipes…"
                                    value={deletedSearch}
                                    onChange={e => setDeletedSearch(e.target.value)}
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                    }}
                                />
                                {filtered.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No matches.</Typography>
                                ) : (
                                    <Box sx={{ maxHeight: 336, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                        {filtered.map((r, i) => (
                                            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: i < filtered.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {r.recipe_type}{r.deleted_at ? ` · Deleted ${new Date(r.deleted_at).toLocaleDateString()}` : ''}
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={restoringId === r.id}
                                                    onClick={() => handleRestore(r.id)}
                                                >
                                                    {restoringId === r.id ? <CircularProgress size={16} color="inherit" /> : 'Restore'}
                                                </Button>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </>
                        );
                    })()}
                </>
            )}
        </Container>
    );
};

export default AdminPage;
